import { createEffect, type JSX, on, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import { browserPushRuntime, type PushRuntime } from '../../lib/push';
import { createPushToggle, PUSH_FAILURE_KEY, type PushToggleStatus } from '../../lib/push-toggle';
import { Panel } from '../Panel';
import { Switch } from '../Switch';

// "Turn on for this device": the Notifications page's switch over the Web Push
// state machine (`lib/push-toggle.ts`), the same machine the topbar bell drives.
//
// The state is always real TEXT beside the switch — off, on, working, blocked
// by the browser, not supported here. Never colour alone and never a tooltip:
// the switch's off track is about 1.6:1 against the card, so the knob and the
// sentence are what carry the state (`styles/tokens.contrast.test.ts`).
//
// The switch shows `subscribed()`, the last CONFIRMED state, and is never
// disabled while working: the machine itself drops a second click, and
// disabling a focused control throws the keyboard user's focus away. A blocked
// or unsupported browser gets the reason and NO switch — a control that cannot
// work is worse than a sentence.
//
// When an enable throws (`#5884`), the reason is rendered as an alert paragraph
// under the state line, so a person who clicks and gets silence sees WHICH
// step actually failed and what to do about it.

const STATE_KEY: Readonly<Record<Exclude<PushToggleStatus, 'loading'>, string>> = {
  off: 'notifications_settings.browser.state_off',
  on: 'notifications_settings.browser.state_on',
  working: 'notifications_settings.browser.state_working',
  blocked: 'notifications_settings.browser.state_blocked',
  unsupported: 'notifications_settings.browser.state_unsupported',
};

export function BrowserNotifications(props: {
  runtime?: PushRuntime;
  onChange?: () => void;
}): JSX.Element {
  const push = createPushToggle(props.runtime ?? browserPushRuntime(), () => props.onChange?.());

  onMount(() => void push.init());

  // The person activated the switch and the browser then refused (blocked) or could not
  // do it (unsupported): the switch leaves the page, and a focused control that is
  // removed drops focus to <body>. Hand focus to the sentence that now says why, so a
  // keyboard or screen-reader user lands on the reason rather than the top of the page.
  // Only on the working -> refused edge: the initial reflect must never take focus.
  let stateRef: HTMLParagraphElement | undefined;
  createEffect(
    on(
      push.status,
      (status, previous) => {
        if (previous === 'working' && (status === 'blocked' || status === 'unsupported')) {
          queueMicrotask(() => stateRef?.focus());
        }
      },
      { defer: true },
    ),
  );

  const switchable = (): boolean => {
    const status = push.status();
    return status === 'off' || status === 'on' || status === 'working';
  };
  const stateText = (): string => {
    const status = push.status();
    return status === 'loading' ? '' : t(STATE_KEY[status]);
  };
  const failureText = (): string => {
    const reason = push.failure();
    return reason === null ? '' : t(PUSH_FAILURE_KEY[reason]);
  };

  return (
    <Panel title={t('notifications_settings.browser.title')} class="browser-notifications">
      <p class="card__desc">{t('notifications_settings.browser.desc')}</p>
      <div class="browser-notifications__row">
        <Show when={switchable()}>
          <Switch
            checked={push.subscribed()}
            aria-label={t('notifications_settings.browser.switch_label')}
            onActivate={() => void (push.status() === 'off' ? push.enable() : push.disable())}
          />
        </Show>
        <p
          class="browser-notifications__state"
          aria-live="polite"
          tabindex="-1"
          ref={(el) => {
            stateRef = el;
          }}
        >
          {stateText()}
        </p>
      </div>
      <Show when={push.failure() !== null}>
        <p role="alert" class="browser-notifications__failure">
          {failureText()}
        </p>
      </Show>
    </Panel>
  );
}
